import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { LOCALES, LocalizedLocaleConfig } from "@core/configs/translation/locales.config";
import { LocaleConfig, TranslationService, TranslationStoreService } from "@ogs-gmbh/ngx-translate";
import { isTranslationInfo } from "@shared/types/translation-info";
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

@Injectable({ providedIn: "root" })
export class LanguageSelectionService {
  private _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  private _translationService: TranslationService = inject(TranslationService);

  public readonly locales: LocalizedLocaleConfig[] = LOCALES;

  private _currentLocale: WritableSignal<LocaleConfig> = signal(this._translationStoreService.getCurrentLocale());

  private _currentLocale$: BehaviorSubject<LocaleConfig> = new BehaviorSubject<LocaleConfig>(this._currentLocale());

  public currentLocale$: Observable<LocaleConfig> = this._currentLocale$.asObservable();

  constructor () {
    this._translationStoreService.getLocale$().subscribe((newLocal: LocaleConfig) => {
      this._currentLocale.set(newLocal);
      this._currentLocale$.next(newLocal);
    });
  }

  public setLocale (newLocale: LocaleConfig): void {
    const localeRef: LocaleConfig | undefined = this.locales.find((locale: LocaleConfig): boolean => this.compareLocales(locale, newLocale));

    if (localeRef)
      this._translationStoreService.setLocale(localeRef);
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  public compareLocales (objA: LocaleConfig, objB: LocaleConfig): boolean {
    return objA.value === objB.value;
  }

  // eslint-disable-next-line @tseslint/no-explicit-any
  public async getTranslatedTemplateLiteral (strings: TemplateStringsArray, ...values: any[]): Promise<string> {
    let str: string = "";

    for (let i: number = 0, j: number = strings.length; i < j; i++) {
      str += strings[ i ];

      // eslint-disable-next-line @tseslint/no-unsafe-assignment, @tseslint/no-explicit-any
      const value: any = values[ i ];

      if (isTranslationInfo(value)) {
        try {
          const translatedString: string = await firstValueFrom(
            this._translationService.translateTokenByCurrentLocale$(
              value.token,
              value.defaultText,
              value.scope
            )
          );

          str += translatedString;
        } catch {}
      } else if (value !== undefined) str += value;
    }

    return str;
  }
}
